/** Column width in px — the single source of truth for the tracker's column
 *  geometry. MatrixTable interpolates it into the header/body pane
 *  `gridTemplateColumns` templates, and the windowing math divides by it, so
 *  the rendered tracks and the visible-range computation can never drift.
 *  (The 240px label pane is sized purely by `--tracker-label-w` in
 *  matrixTable.module.css — it sits OUTSIDE the body scroller and never
 *  enters this module's math.) */
export const COL_W = 140;
const BUFFER_COLS = 4;

export interface IColRange {
  start: number;
  end: number;
}

/**
 * Columns in the pre-measurement SEED window (useVisibleGrid's initial state,
 * before the scroller element exists to measure). Sized to cover the widest
 * realistic body-pane viewport: 24 columns × 140px ≈ 3,360px of data columns
 * (screen minus the 240px label pane). Wider
 * screens briefly show the seed and self-correct one effect-pass after mount.
 * Must stay small relative to real column counts — the whole point is that the
 * FIRST commit renders O(seed), not O(all columns).
 */
export const SEED_COLS = 24;

/** The seed window for a matrix of `colCount` columns (clamped; `colCount === 0`
 *  yields the deliberately inverted empty range, same as visibleColRange). */
export function seedColRange(colCount: number): IColRange {
  return { start: 0, end: Math.min(colCount, SEED_COLS) - 1 };
}

/**
 * Inclusive [start, end] column indices to render for the BODY pane's
 * horizontal viewport, with a small buffer to avoid pop-in. The body scroller
 * contains only data columns (the label column is a separate pane), so column
 * i spans content-x [i*COL_W, (i+1)*COL_W) and the whole clientWidth is data
 * viewport — plain uniform division on both edges, no label overlay to
 * correct for.
 *
 * `start` is clamped to `end` (`Math.min(start, end)`) to cover far over-scroll:
 * a stale/oversized `scrollLeft` (e.g. right after a filter shrinks colCount,
 * before the scroller's own scrollLeft has re-clamped) can otherwise push
 * `firstVisible - BUFFER_COLS` past `end`, inverting the range. `colCount === 0`
 * is exempt from this clamp on purpose — there is no valid column to clamp to,
 * so the range stays deliberately inverted (`end < start`) and callers render
 * nothing via `columns.slice(start, end + 1)`.
 */
export function visibleColRange(scrollLeft: number, clientWidth: number, colCount: number): IColRange {
  const firstVisible = Math.floor(scrollLeft / COL_W);
  const lastVisible = Math.floor((scrollLeft + clientWidth - 1) / COL_W);
  const start = Math.max(0, firstVisible - BUFFER_COLS);
  const end = Math.min(colCount - 1, lastVisible + BUFFER_COLS);
  return {
    start: colCount === 0 ? start : Math.min(start, end),
    end,
  };
}
